import React, { useState, useEffect } from 'react';
import { Search, Grid, List, Trash2, Download, Eye, Upload, FileText, Folder } from 'lucide-react';
import { PhotoView } from 'react-photo-view';
import { supabase } from '../../lib/supabase';
import { FileUpload } from './FileUpload';

interface StorageFile {
  id: string;
  name: string;
  size: number;
  created_at: string;
  updated_at: string;
  last_accessed_at: string;
  metadata: {
    eTag: string;
    size: number;
    mimetype: string;
    cacheControl: string;
    lastModified: string;
    contentLength: number;
    httpStatusCode: number;
  };
  publicUrl: string;
  type: string;
  lastModified: string;
  folder?: string;
}

interface MediaLibraryProps {
  onSelectFile?: (file: StorageFile) => void;
  selectionMode?: boolean;
  allowedTypes?: string[];
}

export const MediaLibrary: React.FC<MediaLibraryProps> = ({
  onSelectFile,
  selectionMode = false,
  allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
}) => {
  const [files, setFiles] = useState<StorageFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [showUpload, setShowUpload] = useState(false);
  const [filterType, setFilterType] = useState<'all' | 'images' | 'documents'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'date' | 'size'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get files from each folder directly
      // No need to list folders first since we know the folder structure

      const allFiles: StorageFile[] = [];

      // Get files from each folder
      const folderNames = ['uploads', 'profile', 'education', 'certificates', 'achievements'];
      
      for (const folderName of folderNames) {
        const { data: folderFiles, error: folderError } = await supabase.storage
          .from('portfolio-images')
          .list(folderName, {
            limit: 100,
            offset: 0,
            sortBy: { column: 'created_at', order: 'desc' }
          });

        if (folderError) {
          console.warn(`Error fetching files from ${folderName}:`, folderError);
          continue;
        }

        if (folderFiles && folderFiles.length > 0) {
          const filesWithUrls = await Promise.all(
            folderFiles.map(async (file) => {
              const filePath = `${folderName}/${file.name}`;
              const { data: urlData } = supabase.storage
                .from('portfolio-images')
                .getPublicUrl(filePath);

              return {
                name: filePath, // Include folder path in name
                id: file.id,
                size: file.metadata?.size || 0,
                type: file.metadata?.mimetype || 'unknown',
                lastModified: file.updated_at,
                publicUrl: urlData.publicUrl,
                created_at: file.created_at,
                updated_at: file.updated_at,
                last_accessed_at: file.last_accessed_at,
                metadata: file.metadata || {},
                folder: folderName // Add folder info for filtering
              } as StorageFile & { folder: string };
            })
          );
          
          allFiles.push(...filesWithUrls);
        }
      }

      setFiles(allFiles);
    } catch (err) {
      console.error('Error fetching files:', err);
      setError('Gagal memuat file dari storage');
    } finally {
      setLoading(false);
    }
  };

  const deleteFile = async (fileName: string) => {
    if (!confirm('Apakah Anda yakin ingin menghapus file ini?')) {
      return;
    }

    try {
      const { error } = await supabase.storage
        .from('portfolio-images')
        .remove([fileName]); // fileName already includes folder path

      if (error) throw error;

      setFiles(prev => prev.filter(file => file.name !== fileName));
      setSelectedFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(fileName);
        return newSet;
      });
    } catch (err) {
      console.error('Error deleting file:', err);
      setError('Gagal menghapus file');
    }
  };

  const deleteSelectedFiles = async () => {
    if (selectedFiles.size === 0) return;

    if (!confirm(`Apakah Anda yakin ingin menghapus ${selectedFiles.size} file?`)) {
      return;
    }

    try {
      const { error } = await supabase.storage
        .from('portfolio-images')
        .remove(Array.from(selectedFiles));

      if (error) throw error;

      setFiles(prev => prev.filter(file => !selectedFiles.has(file.name)));
      setSelectedFiles(new Set());
    } catch (err) {
      console.error('Error deleting files:', err);
      setError('Gagal menghapus file');
    }
  };

  const downloadFile = async (file: StorageFile) => {
    try {
      const { data, error } = await supabase.storage
        .from('portfolio-images')
        .download(file.name); // file.name already includes folder path

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      // Extract just the filename for download (without folder path)
      const fileName = file.name.split('/').pop() || file.name;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading file:', err);
      setError('Gagal mengunduh file');
    }
  };

  const toggleFileSelection = (fileName: string) => {
    setSelectedFiles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fileName)) {
        newSet.delete(fileName);
      } else {
        newSet.add(fileName);
      }
      return newSet;
    });
  };

  const selectAllFiles = () => {
    if (selectedFiles.size === filteredFiles.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(filteredFiles.map(file => file.name)));
    }
  };

  const isImage = (file: StorageFile) => {
    return file.metadata?.mimetype?.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(file.name);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter and sort files
  const filteredFiles = files
    .filter(file => {
      const matchesSearch = file.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = filterType === 'all' || 
        (filterType === 'images' && isImage(file)) ||
        (filterType === 'documents' && !isImage(file));
      const matchesAllowedTypes = allowedTypes.length === 0 || 
        allowedTypes.some(type => file.metadata?.mimetype === type);
      
      return matchesSearch && matchesType && matchesAllowedTypes;
    })
    .sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'date':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'size':
          comparison = a.size - b.size;
          break;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const content = (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {selectionMode ? 'Pilih File' : 'Media Library'}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Kelola file yang tersimpan di Supabase Storage
            </p>
          </div>
          <button
            onClick={() => setShowUpload(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload File
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Upload Modal */}
      {showUpload && (
        <div className="mb-6 bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Upload File Baru</h3>
              <button
                onClick={() => setShowUpload(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          <div className="p-6">
            <FileUpload
              onUploadComplete={() => {
                fetchFiles();
                setShowUpload(false);
              }}
              onUploadError={(error) => setError(error)}
              folder="uploads"
              acceptedTypes={allowedTypes}
              maxSizeInMB={10}
            />
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="mb-6 bg-white shadow rounded-lg p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Cari file..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-2">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as any)}
              className="block px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Semua File</option>
              <option value="images">Gambar</option>
              <option value="documents">Dokumen</option>
            </select>

            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [sort, order] = e.target.value.split('-');
                setSortBy(sort as any);
                setSortOrder(order as any);
              }}
              className="block px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="date-desc">Terbaru</option>
              <option value="date-asc">Terlama</option>
              <option value="name-asc">Nama A-Z</option>
              <option value="name-desc">Nama Z-A</option>
              <option value="size-desc">Ukuran Besar</option>
              <option value="size-asc">Ukuran Kecil</option>
            </select>

            <div className="flex border border-gray-300 rounded-md">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-blue-50 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <Grid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 border-l border-gray-300 ${viewMode === 'list' ? 'bg-blue-50 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Bulk actions */}
        {!selectionMode && (
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedFiles.size === filteredFiles.length && filteredFiles.length > 0}
                  onChange={selectAllFiles}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-gray-600">
                  Pilih semua ({filteredFiles.length})
                </span>
              </label>
              
              {selectedFiles.size > 0 && (
                <span className="text-sm text-gray-600">
                  {selectedFiles.size} file dipilih
                </span>
              )}
            </div>
            
            {selectedFiles.size > 0 && (
              <button
                onClick={deleteSelectedFiles}
                className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Hapus Terpilih
              </button>
            )}
          </div>
        )}
      </div>

      {/* Files */}
      <div className="bg-white shadow rounded-lg">
        {filteredFiles.length === 0 ? (
          <div className="p-12 text-center">
            <Folder className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {searchTerm ? 'Tidak ada file yang ditemukan' : 'Belum ada file'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Coba ubah kata kunci pencarian' : 'Mulai dengan mengupload file pertama'}
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="p-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {filteredFiles.map((file) => (
                <div
                  key={file.name}
                  className={`relative group border-2 rounded-lg overflow-hidden cursor-pointer transition-all ${
                    selectedFiles.has(file.name) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => {
                    if (selectionMode && onSelectFile) {
                      onSelectFile(file);
                    } else {
                      toggleFileSelection(file.name);
                    }
                  }}
                >
                  {/* Selection checkbox */}
                  {!selectionMode && (
                    <div className="absolute top-2 left-2 z-10">
                      <input
                        type="checkbox"
                        checked={selectedFiles.has(file.name)}
                        onChange={() => toggleFileSelection(file.name)}
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  )}

                  {/* File preview */}
                  <div className="aspect-square bg-gray-100 flex items-center justify-center">
                    {isImage(file) ? (
                      <PhotoView src={file.publicUrl}>
                        <img
                          src={file.publicUrl}
                          alt={file.name}
                          className="w-full h-full object-cover cursor-pointer"
                        />
                      </PhotoView>
                    ) : (
                      <FileText className="h-8 w-8 text-gray-400" />
                    )}
                  </div>

                  {/* File info */}
                  <div className="p-2">
                    <p className="text-xs font-medium text-gray-900 truncate" title={file.name}>
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.size)}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(file.publicUrl, '_blank');
                        }}
                        className="p-1 bg-white rounded shadow-sm hover:bg-gray-50"
                        title="Lihat"
                      >
                        <Eye className="h-3 w-3 text-gray-600" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadFile(file);
                        }}
                        className="p-1 bg-white rounded shadow-sm hover:bg-gray-50"
                        title="Download"
                      >
                        <Download className="h-3 w-3 text-gray-600" />
                      </button>
                      {!selectionMode && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteFile(file.name);
                          }}
                          className="p-1 bg-white rounded shadow-sm hover:bg-gray-50"
                          title="Hapus"
                        >
                          <Trash2 className="h-3 w-3 text-red-600" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {!selectionMode && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={selectedFiles.size === filteredFiles.length && filteredFiles.length > 0}
                        onChange={selectAllFiles}
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      />
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Preview
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nama
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ukuran
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tanggal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aksi
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredFiles.map((file) => (
                  <tr
                    key={file.name}
                    className={`hover:bg-gray-50 cursor-pointer ${
                      selectedFiles.has(file.name) ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => {
                      if (selectionMode && onSelectFile) {
                        onSelectFile(file);
                      } else {
                        toggleFileSelection(file.name);
                      }
                    }}
                  >
                    {!selectionMode && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedFiles.has(file.name)}
                          onChange={() => toggleFileSelection(file.name)}
                          className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                        {isImage(file) ? (
                          <PhotoView src={file.publicUrl}>
                            <img
                              src={file.publicUrl}
                              alt={file.name}
                              className="w-full h-full object-cover rounded cursor-pointer"
                            />
                          </PhotoView>
                        ) : (
                          <FileText className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 truncate max-w-xs" title={file.name}>
                        {file.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatFileSize(file.size)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(file.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(file.publicUrl, '_blank');
                          }}
                          className="text-blue-600 hover:text-blue-900"
                          title="Lihat"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadFile(file);
                          }}
                          className="text-green-600 hover:text-green-900"
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        {!selectionMode && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteFile(file.name);
                            }}
                            className="text-red-600 hover:text-red-900"
                            title="Hapus"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  if (selectionMode) {
    return content;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="py-6">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {content}
        </div>
      </div>
    </div>
  );
};

// Standalone media library page
export const MediaLibraryPage: React.FC = () => {
  return <MediaLibrary />;
};

// Media picker modal
interface MediaPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (file: StorageFile) => void;
  allowedTypes?: string[];
}

export const MediaPicker: React.FC<MediaPickerProps> = ({
  isOpen,
  onClose,
  onSelect,
  allowedTypes
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Pilih File</h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="max-h-96 overflow-y-auto">
              <MediaLibrary
                selectionMode={true}
                onSelectFile={(file) => {
                  onSelect(file);
                  onClose();
                }}
                allowedTypes={allowedTypes}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};